import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, FileText, ArrowUpRight, RefreshCw, Edit2, Check, Mic, MessageSquareQuote, AlertCircle } from 'lucide-react';

export default function SessionSynthesisGenerator({ session, sessionIssues, onSynthesisReady }) {
  const [generating, setGenerating] = useState(false);
  const [synthesis, setSynthesis] = useState(null);
  const [upwardReport, setUpwardReport] = useState(null);
  const [editingSynthesis, setEditingSynthesis] = useState(false);
  const [editingUpward, setEditingUpward] = useState(false);
  const [synthText, setSynthText] = useState('');
  const [upwardText, setUpwardText] = useState('');
  const [autoGenerated, setAutoGenerated] = useState(false);

  // Transcript state
  const [transcriptUrl, setTranscriptUrl] = useState('');
  const [transcriptUsed, setTranscriptUsed] = useState(false);
  const [transcriptSource, setTranscriptSource] = useState(null);
  const [showTranscriptInput, setShowTranscriptInput] = useState(false);

  // Auto-generate on mount if no existing data
  useEffect(() => {
    if (!autoGenerated && sessionIssues.length > 0 && !session?.ai_session_synthesis) {
      handleGenerate({ autoFind: true });
      setAutoGenerated(true);
    }
  }, [sessionIssues.length]);

  const handleGenerate = async ({ withUrl = false, autoFind = false } = {}) => {
    if (!session?.id) return;
    setGenerating(true);
    try {
      const payload = { session_id: session.id };

      if (withUrl && transcriptUrl.trim()) {
        payload.transcript_url = transcriptUrl.trim();
      } else if (autoFind) {
        payload.auto_find_transcript = true;
      }

      const response = await base44.functions.invoke('councilSessionSynthesis', payload);
      const data = response.data || response;

      setSynthesis(data.session_synthesis);
      setUpwardReport(data.upward_report);
      setTranscriptUsed(data.transcript_used || false);
      setTranscriptSource(data.transcript_source || null);

      // Build editable text
      const synth = data.session_synthesis;
      if (synth) {
        let text = `${synth.summary || ''}\n\n`;
        if (synth.classification_breakdown) text += `CLASSIFICATION BREAKDOWN:\n${synth.classification_breakdown}\n\n`;
        if (synth.pattern_observations) text += `PATTERNS:\n${synth.pattern_observations}\n\n`;
        if (synth.risk_items) text += `RISK ITEMS:\n${synth.risk_items}\n\n`;
        if (synth.action_items_summary) text += `ACTION ITEMS:\n${synth.action_items_summary}\n\n`;
        if (synth.verbal_insights) text += `VERBAL INSIGHTS (from transcript):\n${synth.verbal_insights}\n\n`;
        if (synth.unprocessed_items) text += `UNPROCESSED ITEMS (discussed but not triaged):\n${synth.unprocessed_items}\n\n`;
        if (synth.prior_followup) text += `PRIOR FOLLOW-UP:\n${synth.prior_followup}\n\n`;
        if (synth.next_focus) text += `NEXT SESSION FOCUS:\n${synth.next_focus}`;
        setSynthText(text);
      }

      const upward = data.upward_report;
      if (upward) {
        let text = `${upward.headline || ''}\n\n`;
        text += `KEY FINDINGS:\n${(upward.key_findings || []).map((f, i) => `${i + 1}. ${f}`).join('\n')}\n\n`;
        if (upward.strategic_implications) text += `STRATEGIC IMPLICATIONS:\n${upward.strategic_implications}\n\n`;
        if (upward.recommended_actions?.length) text += `RECOMMENDED ACTIONS:\n${upward.recommended_actions.map((a, i) => `${i + 1}. ${a}`).join('\n')}`;
        setUpwardText(text);
      }

      onSynthesisReady?.(data);
    } catch (err) {
      console.error('Synthesis generation failed:', err);
    } finally {
      setGenerating(false);
    }
  };

  if (generating) {
    return (
      <Card className="glass-panel border-purple-500/20">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-3" />
          <p className="text-sm text-purple-300">AI is generating session synthesis and upward report...</p>
          <p className="text-xs text-slate-500 mt-1">
            Analyzing {sessionIssues.length} reviewed issues
            {transcriptUrl.trim() && ' + meeting transcript'}
          </p>
        </CardContent>
      </Card>);

  }

  // Pre-synthesis state: show transcript option + generate button
  if (!synthesis && !upwardReport) {
    return (
      <Card className="glass-panel border-purple-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-purple-400 flex items-center gap-2">
            <FileText className="w-4 h-4" /> AI Session Synthesis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-400">
            Generate an AI synthesis of this session's decisions and a strategic upward report.
            Optionally include a Google Meet transcript for richer analysis.
          </p>

          {/* Transcript Input */}
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-700 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400 flex items-center gap-1.5">
                <Mic className="w-3 h-3" /> Google Meet Transcript (optional)
              </p>
              <Button
                size="sm" variant="ghost"
                onClick={() => setShowTranscriptInput(!showTranscriptInput)}
                className="h-6 text-[10px] text-purple-400 hover:text-purple-300">

                {showTranscriptInput ? 'Hide' : 'Add transcript'}
              </Button>
            </div>

            {showTranscriptInput &&
            <div className="space-y-2">
                <Input data-testid="hc-sessionsynthesisgenerator-input"
                value={transcriptUrl}
                onChange={(e) => setTranscriptUrl(e.target.value)}
                placeholder="Paste Google Doc transcript URL..." className="bg-zinc-50 text-slate-200 px-3 py-1 text-xs rounded-md flex w-full border shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm border-slate-600 h-8 placeholder:text-slate-500" />


                <p className="text-[10px] text-slate-500">
                  Paste the link to the Google Doc transcript from your Meet session.
                  Or leave blank — we'll auto-search your Drive for a transcript matching the session date.
                </p>
              </div>
            }
          </div>

          {/* Generate Button */}
          <Button
            onClick={() => handleGenerate({ withUrl: !!transcriptUrl.trim(), autoFind: !transcriptUrl.trim() })}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white h-9 text-sm">

            <FileText className="w-4 h-4 mr-2" />
            {transcriptUrl.trim() ? 'Generate with Transcript' : 'Generate Synthesis'}
          </Button>
        </CardContent>
      </Card>);

  }

  // Post-synthesis state: show results
  return (
    <div className="space-y-4">
      {/* Transcript Status Banner */}
      {transcriptUsed &&
      <div className="flex items-center gap-2 p-2 rounded-lg bg-purple-950/20 border border-purple-500/20">
          <Mic className="w-4 h-4 text-purple-400" />
          <span className="text-xs text-purple-300 flex-1">
            Transcript-enhanced synthesis
            {transcriptSource && <span className="text-slate-500 ml-1">({transcriptSource})</span>}
          </span>
          <Badge className="bg-purple-500/20 text-purple-300 text-[10px]">Sonnet</Badge>
        </div>
      }
      {!transcriptUsed && synthesis &&
      <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/40 border border-slate-700">
          <AlertCircle className="w-3.5 h-3.5 text-slate-500" />
          <span className="text-xs text-slate-400 flex-1">Data-only synthesis. Re-run with a meeting transcript for richer analysis.</span>
          <Button
          size="sm" variant="ghost"
          onClick={() => {setShowTranscriptInput(true);setSynthesis(null);setUpwardReport(null);}}
          className="h-6 text-[10px] text-purple-400 hover:text-purple-300">

            <Mic className="w-3 h-3 mr-1" /> Add transcript
          </Button>
        </div>
      }

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Session Synthesis */}
        <Card className="glass-panel border-cyan-500/20">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-cyan-400 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Session Synthesis
              </CardTitle>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setEditingSynthesis(!editingSynthesis)} className="h-6 w-6 p-0 text-slate-400 hover:text-slate-200">
                  {editingSynthesis ? <Check className="w-3 h-3" /> : <Edit2 className="w-3 h-3" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleGenerate({ withUrl: !!transcriptUrl.trim(), autoFind: !transcriptUrl.trim() })} className="h-6 w-6 p-0 text-slate-400 hover:text-slate-200">
                  <RefreshCw className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {editingSynthesis ?
            <Textarea data-testid="hc-sessionsynthesisgenerator-textarea" value={synthText} onChange={(e) => setSynthText(e.target.value)} className="bg-slate-800/60 border-slate-700 text-slate-200 min-h-[300px] text-sm" /> :
            synthesis ?
            <div className="space-y-3 text-sm">
                <p className="text-slate-600">{synthesis.summary}</p>

                {synthesis.pattern_observations &&
              <div className="bg-zinc-50 text-slate-700 p-2 rounded border border-cyan-500/10">
                    <p className="text-xs font-semibold text-cyan-400 mb-1">Patterns</p>
                    <p className="text-slate-700 text-xs">{synthesis.pattern_observations}</p>
                  </div>
              }

                {synthesis.risk_items &&
              <div className="p-2 rounded bg-red-950/20 border border-red-500/10">
                    <p className="text-xs font-semibold text-red-400 mb-1">Risk Items</p>
                    <p className="text-slate-700 text-xs">{synthesis.risk_items}</p>
                  </div>
              }

                {synthesis.action_items_summary &&
              <div className="p-2 rounded bg-slate-800/40">
                    <p className="text-slate-700 mb-1 text-xs font-semibold">Action Items</p>
                    <p className="text-slate-700 text-xs">{synthesis.action_items_summary}</p>
                  </div>
              }

                {/* Transcript-only sections */}
                {synthesis.verbal_insights &&
              <div className="p-2 rounded bg-purple-950/20 border border-purple-500/10">
                    <p className="text-xs font-semibold text-purple-400 mb-1 flex items-center gap-1">
                      <MessageSquareQuote className="w-3 h-3" /> Verbal Insights
                    </p>
                    <p className="text-slate-300 text-xs">{synthesis.verbal_insights}</p>
                  </div>
              }

                {synthesis.unprocessed_items &&
              <div className="p-2 rounded bg-amber-950/20 border border-amber-500/10">
                    <p className="text-xs font-semibold text-amber-400 mb-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Discussed but Not Processed
                    </p>
                    <p className="text-slate-300 text-xs">{synthesis.unprocessed_items}</p>
                  </div>
              }

                {synthesis.next_focus &&
              <div className="p-2 rounded bg-indigo-950/20 border border-indigo-500/10">
                    <p className="text-xs font-semibold text-indigo-400 mb-1">Next Session Focus</p>
                    <p className="text-slate-600 text-xs">{synthesis.next_focus}</p>
                  </div>
              }
              </div> :
            null}
          </CardContent>
        </Card>

        {/* Upward Report */}
        <Card className="glass-panel border-amber-500/20">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-amber-400 flex items-center gap-2">
                <ArrowUpRight className="w-4 h-4" /> Upward Report
              </CardTitle>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setEditingUpward(!editingUpward)} className="h-6 w-6 p-0 text-slate-400 hover:text-slate-200">
                  {editingUpward ? <Check className="w-3 h-3" /> : <Edit2 className="w-3 h-3" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleGenerate({ withUrl: !!transcriptUrl.trim(), autoFind: !transcriptUrl.trim() })} className="h-6 w-6 p-0 text-slate-400 hover:text-slate-200">
                  <RefreshCw className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {editingUpward ?
            <Textarea data-testid="hc-sessionsynthesisgenerator-textarea-1" value={upwardText} onChange={(e) => setUpwardText(e.target.value)} className="bg-slate-800/60 border-slate-700 text-slate-200 min-h-[300px] text-sm" /> :
            upwardReport ?
            <div className="space-y-3 text-sm">
                {upwardReport.headline && <p className="text-amber-400 text-base font-semibold">{upwardReport.headline}</p>}

                {upwardReport.key_findings?.length > 0 &&
              <div className="space-y-1">
                    <p className="text-xs font-semibold text-amber-400">Key Findings</p>
                    {upwardReport.key_findings.map((finding, idx) =>
                <div key={idx} className="flex gap-2 text-xs text-slate-300">
                        <span className="text-amber-500 font-bold flex-shrink-0">{idx + 1}.</span>
                        <span className="text-slate-700">{finding}</span>
                      </div>
                )}
                  </div>
              }

                {upwardReport.strategic_implications &&
              <div className="p-2 rounded bg-amber-950/20 border border-amber-500/10">
                    <p className="text-amber-600 mb-1 text-xs font-semibold">Strategic Implications</p>
                    <p className="text-slate-700 text-xs">{upwardReport.strategic_implications}</p>
                  </div>
              }

                {upwardReport.recommended_actions?.length > 0 &&
              <div className="space-y-1">
                    <p className="text-xs font-semibold text-amber-400">Recommended Actions</p>
                    {upwardReport.recommended_actions.map((action, idx) =>
                <div key={idx} className="flex gap-2 text-xs text-slate-300">
                        <span className="text-amber-500 font-bold flex-shrink-0">→</span>
                        <span className="text-slate-700">{action}</span>
                      </div>
                )}
                  </div>
              }
              </div> :
            null}
          </CardContent>
        </Card>
      </div>
    </div>);

}